import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Search,
  Receipt,
  Plus,
  Pencil,
  Trash2,
  FileText,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Sheet,
  Lock,
  AlertTriangle,
  X,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import DateRangePicker from "../components/DateRangePicker";
import SearchableDropdown from "../components/SearchableDropdown";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import ExpenseModal from "./ExpenseModal";
import { expensesApi, eventsApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl, byNewest } from "@/lib/utils";
import { type ExpenseRecord, type PaymentMethodOption, listFrom, paymentMethodLabel } from "./expenseUtils";

type SortCol = "title" | "category" | "date" | "amount";
type Attention = "uncategorised" | "no_receipt" | null;

interface Summary {
  this_month_total: number;
  this_month_count: number;
  last_month_total: number;
  uncategorised_count: number;
  missing_receipt_count: number;
  receipt_required_above: number;
}

const MOBILE_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "date:desc", label: "Date (latest)" },
  { value: "date:asc", label: "Date (earliest)" },
  { value: "amount:desc", label: "Amount (high to low)" },
  { value: "amount:asc", label: "Amount (low to high)" },
  { value: "title:asc", label: "Title (A-Z)" },
  { value: "category:asc", label: "Category (A-Z)" },
];

function SortTh({ col, label, align = "left", sortCol, sortDir, onSort, className = "" }: {
  col: SortCol; label: string; align?: "left" | "right";
  sortCol: SortCol | null; sortDir: "asc" | "desc"; onSort: (c: SortCol) => void; className?: string;
}) {
  const Icon = sortCol !== col ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th
      onClick={() => onSort(col)}
      aria-sort={sortCol === col ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
      className={`${align === "right" ? "text-right" : "text-left"} px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide cursor-pointer select-none hover:bg-amber-50 transition-colors ${className}`}
    >
      {label}
      <Icon className={`inline w-3 h-3 ml-0.5 ${sortCol === col ? "text-amber-600" : "opacity-30"}`} />
    </th>
  );
}

const amountOf = (e: ExpenseRecord) => Number(e.amount) || 0;
const monthChange = (now: number, before: number) => (before > 0 ? Math.round(((now - before) / before) * 100) : null);

export default function ExpensesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "master" || user?.role === "super_admin";

  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [events, setEvents] = useState<{ id: number; name: string }[]>([]);
  const [methods, setMethods] = useState<PaymentMethodOption[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  // "all" | "none" (uncategorised) | "auto" (vendor-payment copies) | category id
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [attention, setAttention] = useState<Attention>(null);
  // null = default "newest first" (created_at desc)
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRecord | null>(null);
  const [deleting, setDeleting] = useState<ExpenseRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const threshold = summary?.receipt_required_above ?? 0;

  const loadData = async () => {
    try {
      const [expRes, sumRes, optRes] = await Promise.all([expensesApi.list(), expensesApi.summary(), expensesApi.formOptions()]);
      setExpenses(listFrom<ExpenseRecord>(expRes));
      setSummary(sumRes?.data ?? null);
      setMethods(optRes?.data?.payment_methods ?? []);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't load expenses"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    eventsApi.list().then((res) => setEvents(listFrom<{ id: number; name: string }>(res))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (col: SortCol) => {
    if (sortCol !== col) { setSortCol(col); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortCol(null); setSortDir("asc"); }
  };
  const handleMobileSort = (value: string) => {
    if (value === "newest") { setSortCol(null); setSortDir("asc"); return; }
    const [col, dir] = value.split(":") as [SortCol, "asc" | "desc"];
    setSortCol(col);
    setSortDir(dir);
  };

  const needsReceipt = (e: ExpenseRecord) => !e.is_vendor_payment && !e.proof_path && (threshold <= 0 || amountOf(e) > threshold);

  // Everything except the category chip, so the chips can show what each category would add up to
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter((e) => {
      if (q) {
        const hay = [e.title, e.description, e.category?.name, e.vendor?.name, e.event?.name, e.paid_to, e.reference_number, e.notes]
          .map((v) => (v || "").toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      const d = (e.expense_date || "").slice(0, 10);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      if (eventFilter !== "all" && String(e.event_id ?? "") !== eventFilter) return false;
      if (methodFilter !== "all" && (e.payment_method || "") !== methodFilter) return false;
      if (attention === "uncategorised" && (e.is_vendor_payment || e.expense_category_id)) return false;
      if (attention === "no_receipt" && !needsReceipt(e)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, search, fromDate, toDate, eventFilter, methodFilter, attention, threshold]);

  const categoryChips = useMemo(() => {
    const byCat = new Map<string, { key: string; name: string; total: number; count: number }>();
    let autoCount = 0;
    let autoTotal = 0;
    let allTotal = 0;
    for (const e of baseFiltered) {
      if (e.is_vendor_payment) { autoCount++; autoTotal += amountOf(e); continue; }
      allTotal += amountOf(e);
      const key = e.expense_category_id ? String(e.expense_category_id) : "none";
      const name = e.category?.name || "Uncategorised";
      const entry = byCat.get(key) || { key, name, total: 0, count: 0 };
      entry.total += amountOf(e);
      entry.count++;
      byCat.set(key, entry);
    }
    return { cats: [...byCat.values()].sort((a, b) => b.total - a.total), allTotal, autoCount, autoTotal };
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    let list = baseFiltered;
    if (categoryFilter === "auto") list = list.filter((e) => e.is_vendor_payment);
    else if (categoryFilter === "none") list = list.filter((e) => !e.is_vendor_payment && !e.expense_category_id);
    else if (categoryFilter !== "all") list = list.filter((e) => String(e.expense_category_id) === categoryFilter);

    if (!sortCol) return [...list].sort(byNewest);
    return [...list].sort((a, b) => {
      let cmp = 0;
      if (sortCol === "title") cmp = (a.title || "").localeCompare(b.title || "");
      else if (sortCol === "category") cmp = (a.category?.name || "").localeCompare(b.category?.name || "");
      else if (sortCol === "date") cmp = (a.expense_date || "").localeCompare(b.expense_date || "");
      else cmp = amountOf(a) - amountOf(b);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [baseFiltered, categoryFilter, sortCol, sortDir]);

  // Vendor-payment copies are listed but never added: that money is already counted as vendor cost
  const counted = filtered.filter((e) => !e.is_vendor_payment);
  const total = counted.reduce((s, e) => s + amountOf(e), 0);
  const hasFilters = !!(search || fromDate || toDate || eventFilter !== "all" || methodFilter !== "all" || categoryFilter !== "all" || attention);

  const clearFilters = () => {
    setSearch(""); setFromDate(""); setToDate(""); setEventFilter("all"); setMethodFilter("all"); setCategoryFilter("all"); setAttention(null);
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (e: ExpenseRecord) => { setEditing(e); setModalOpen(true); };

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await expensesApi.delete(deleting.id);
      showToast("Expense deleted", "success");
      setDeleting(null);
      loadData();
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't delete the expense"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const exportToExcel = () => {
    const rows = [
      ["#", "Date", "Title", "Category", "Event", "Paid to", "Paid via", "Reference", "Receipt", "Counted in totals", "Amount (₹)"],
      ...filtered.map((e, i) => [
        i + 1,
        (e.expense_date || "").slice(0, 10),
        e.title || "",
        e.category?.name || "Uncategorised",
        e.event?.name || "",
        e.paid_to || e.vendor?.name || "",
        paymentMethodLabel(e.payment_method, methods),
        e.reference_number || "",
        e.proof_path ? "Yes" : "No",
        e.is_vendor_payment ? "No (vendor payment)" : "Yes",
        amountOf(e),
      ]),
      ["TOTAL", "", "", "", "", "", "", "", "", "", total],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 5 }, { wch: 12 }, { wch: 30 }, { wch: 20 }, { wch: 24 }, { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 9 }, { wch: 20 }, { wch: 14 }];
    ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2", sqref: "A2" }];
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let r = 1; r <= range.e.r; r++) {
      const ref = `K${r + 1}`;
      if (ws[ref] && typeof ws[ref].v === "number") ws[ref].z = "#,##0.00";
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    XLSX.writeFile(wb, `expenses_${new Date().toISOString().split("T")[0]}.xlsx`);
    showToast(`Exported ${filtered.length} expenses`, "success");
  };

  const change = summary ? monthChange(summary.this_month_total, summary.last_month_total) : null;
  const attentionCount = (summary?.uncategorised_count ?? 0) + (summary?.missing_receipt_count ?? 0);

  const chipCls = (active: boolean) =>
    `shrink-0 min-h-10 px-3.5 rounded-full border text-sm whitespace-nowrap transition-colors ${
      active ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
    }`;

  const categoryPill = (e: ExpenseRecord) =>
    e.is_vendor_payment ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
        <Lock className="w-3 h-3" /> Vendor payment
      </span>
    ) : e.category ? (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800">{e.category.name}</span>
    ) : (
      <button type="button" onClick={() => openEdit(e)} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 hover:bg-red-100">
        No category · add one
      </button>
    );

  const receiptCell = (e: ExpenseRecord, mobile = false) =>
    e.proof_path ? (
      <a href={getImageUrl(e.proof_path)} target="_blank" rel="noopener noreferrer" aria-label="View receipt"
        className={mobile
          ? "h-10 px-3 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
          : "inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800"}>
        <FileText className="w-4 h-4" />{mobile && "Receipt"}<ExternalLink className="w-3 h-3" />
      </a>
    ) : needsReceipt(e) ? (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">No receipt</span>
    ) : mobile ? null : <span className="text-stone-300">-</span>;

  const actions = (e: ExpenseRecord, mobile = false) =>
    e.is_auto ? null : (
      <div className={`flex items-center ${mobile ? "gap-2" : "justify-center gap-1"}`}>
        <button onClick={() => openEdit(e)} aria-label={`Edit ${e.title}`} title="Edit"
          className={mobile ? "h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:text-amber-600 hover:bg-amber-50" : "p-1.5 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg"}>
          <Pencil className="w-4 h-4" />
        </button>
        {canDelete && (
          <button onClick={() => setDeleting(e)} aria-label={`Delete ${e.title}`} title="Delete"
            className={mobile ? "h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:text-red-600 hover:bg-red-50" : "p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg"}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );

  const lockNote = (
    <p className="text-xs text-slate-600">
      Created from a vendor payment and not added to the totals (it's already counted as vendor cost). Change it in{" "}
      <Link to="/vendor-payments" className="font-semibold text-amber-700 hover:underline">Vendor Payments</Link>.
    </p>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* The Topbar shows this subtitle from lg up */}
        <p className="text-sm text-stone-500 lg:hidden">Record day-to-day spending and link it to events.</p>
        <div className="flex gap-2 sm:ml-auto">
          <button onClick={exportToExcel} disabled={filtered.length === 0}
            className="h-10 flex items-center gap-1.5 px-3 rounded-xl text-sm font-semibold bg-white border border-stone-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
            <Sheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={openCreate} className="h-10 flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm">
            <Plus className="w-4 h-4" /> Add expense
          </button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-xl border border-stone-200 px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">This month</p>
            <p className="text-2xl font-bold text-stone-800 tabular-nums">{formatCurrencyFull(summary.this_month_total)}</p>
            <p className="text-xs text-stone-500">{summary.this_month_count} expense{summary.this_month_count === 1 ? "" : "s"}</p>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">vs last month</p>
            <p className={`text-2xl font-bold tabular-nums ${change === null ? "text-stone-400" : change > 0 ? "text-red-600" : "text-emerald-600"}`}>
              {change === null ? "—" : `${change > 0 ? "+" : ""}${change}%`}
            </p>
            <p className="text-xs text-stone-500">{formatCurrencyFull(summary.last_month_total)} last month</p>
          </div>
          <div className={`rounded-xl border px-4 py-3 ${attentionCount ? "bg-amber-50 border-amber-200" : "bg-white border-stone-200"}`}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400 flex items-center gap-1">
              {attentionCount > 0 && <AlertTriangle className="w-3 h-3 text-amber-600" />} Needs attention
            </p>
            {attentionCount === 0 ? (
              <p className="text-sm text-emerald-700 font-medium mt-1">All expenses have a category and receipt.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-1.5">
                {summary.uncategorised_count > 0 && (
                  <button type="button" onClick={() => setAttention(attention === "uncategorised" ? null : "uncategorised")}
                    className={`min-h-9 px-3 rounded-lg text-xs font-semibold border ${attention === "uncategorised" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-800 border-amber-300 hover:bg-amber-100"}`}>
                    {summary.uncategorised_count} without a category
                  </button>
                )}
                {summary.missing_receipt_count > 0 && (
                  <button type="button" onClick={() => setAttention(attention === "no_receipt" ? null : "no_receipt")}
                    className={`min-h-9 px-3 rounded-lg text-xs font-semibold border ${attention === "no_receipt" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-800 border-amber-300 hover:bg-amber-100"}`}>
                    {summary.missing_receipt_count} without a receipt{threshold > 0 ? ` (over ${formatCurrencyFull(threshold)})` : ""}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-200 p-3 sm:p-4 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label htmlFor="expense-search" className="block text-xs font-medium text-stone-500 mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input id="expense-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title, paid to, reference…"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none" />
            </div>
          </div>
          <DateRangePicker fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} />
          <div>
            <span className="block text-xs font-medium text-stone-500 mb-1.5">Event</span>
            <SearchableDropdown
              options={[{ value: "all", label: "All events" }, ...events.map((e) => ({ value: String(e.id), label: e.name }))]}
              value={eventFilter} onChange={setEventFilter} placeholder="All events" />
          </div>
          <div>
            <label htmlFor="expense-method" className="block text-xs font-medium text-stone-500 mb-1.5">Paid via</label>
            <select id="expense-method" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none">
              <option value="all">Any method</option>
              {methods.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Category chips with what each adds up to */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-2 -mx-1 px-1" role="group" aria-label="Filter by category">
        <button type="button" aria-pressed={categoryFilter === "all"} onClick={() => setCategoryFilter("all")} className={chipCls(categoryFilter === "all")}>
          All <span className="text-stone-400 tabular-nums">{formatCurrencyFull(categoryChips.allTotal)}</span>
        </button>
        {categoryChips.cats.map((c) => (
          <button key={c.key} type="button" aria-pressed={categoryFilter === c.key} onClick={() => setCategoryFilter(categoryFilter === c.key ? "all" : c.key)} className={chipCls(categoryFilter === c.key)}>
            {c.name} <span className="text-stone-400 tabular-nums">{formatCurrencyFull(c.total)}</span>
          </button>
        ))}
        {categoryChips.autoCount > 0 && (
          <button type="button" aria-pressed={categoryFilter === "auto"} onClick={() => setCategoryFilter(categoryFilter === "auto" ? "all" : "auto")} className={chipCls(categoryFilter === "auto")}>
            <Lock className="inline w-3 h-3 mr-1 -mt-0.5" />Vendor payments <span className="text-stone-400">{categoryChips.autoCount}</span>
          </button>
        )}
      </div>

      {hasFilters && (
        <div className="flex items-center justify-between gap-3 mb-3 text-sm">
          <span className="text-stone-500">{filtered.length} of {expenses.length} shown</span>
          <button type="button" onClick={clearFilters} className="h-9 inline-flex items-center gap-1 px-3 rounded-lg text-stone-600 hover:bg-stone-100">
            <X className="w-4 h-4" /> Clear filters
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
          <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-600 font-medium">{expenses.length === 0 ? "No expenses yet" : "No expenses match these filters"}</p>
          {expenses.length === 0 ? (
            <button onClick={openCreate} className="mt-4 h-10 inline-flex items-center gap-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm">
              <Plus className="w-4 h-4" /> Add the first expense
            </button>
          ) : (
            <button onClick={clearFilters} className="mt-3 text-sm font-semibold text-amber-700 hover:underline">Clear filters</button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {/* Mobile cards */}
          <div className="md:hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-100 bg-stone-50">
              <span className="text-xs font-medium text-stone-500">{filtered.length} expense{filtered.length === 1 ? "" : "s"}</span>
              <select value={sortCol ? `${sortCol}:${sortDir}` : "newest"} onChange={(e) => handleMobileSort(e.target.value)} aria-label="Sort expenses"
                className="h-10 max-w-[60%] px-3 rounded-lg border border-stone-200 text-sm bg-white text-stone-700 outline-none focus:border-amber-400">
                {MOBILE_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="divide-y divide-stone-100">
              {filtered.map((e) => (
                <div key={e.id} className={`p-4 ${e.is_vendor_payment ? "bg-stone-50/70" : ""}`}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-stone-800 min-w-0 break-words">{e.title}</p>
                    <p className={`shrink-0 font-bold text-sm tabular-nums ${e.is_vendor_payment ? "text-stone-400" : "text-amber-700"}`}>{formatCurrencyFull(e.amount)}</p>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-500">
                    {categoryPill(e)}
                    <span>{formatDate(e.expense_date)}</span>
                    {e.payment_method && <span>· {paymentMethodLabel(e.payment_method, methods)}</span>}
                    {e.event && <span className="truncate max-w-full">· {e.event.name}</span>}
                    {(e.paid_to || e.vendor) && <span className="truncate max-w-full">· {e.paid_to || e.vendor?.name}</span>}
                  </div>
                  {e.description && !e.is_auto && <p className="mt-1.5 text-xs text-stone-500 line-clamp-2 break-words">{e.description}</p>}
                  {e.is_auto ? (
                    <div className="mt-2">{lockNote}</div>
                  ) : (
                    <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                      {receiptCell(e, true)}
                      {actions(e, true)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border-t-2 border-amber-200">
              <span className="font-bold text-stone-800 text-sm">Total ({counted.length})</span>
              <span className="font-bold text-amber-800 tabular-nums">{formatCurrencyFull(total)}</span>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-amber-50 border-b-2 border-amber-200">
                <tr>
                  <SortTh col="title" label="Expense" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh col="category" label="Category" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh col="date" label="Date" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden lg:table-cell">Event</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden xl:table-cell">Paid to · via</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Receipt</th>
                  <SortTh col="amount" label="Amount" align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-center px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((e) => (
                  <tr key={e.id} className={e.is_vendor_payment ? "bg-stone-50/70" : "bg-white hover:bg-stone-50/60"}>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="font-semibold text-stone-800 text-sm">{e.title}</div>
                      {e.is_auto ? (
                        <div className="mt-0.5">{lockNote}</div>
                      ) : (
                        <>
                          {e.description && <div className="text-xs text-stone-500 mt-0.5 truncate">{e.description}</div>}
                          {e.reference_number && <div className="text-xs text-stone-400 mt-0.5">Ref: {e.reference_number}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">{categoryPill(e)}</td>
                    <td className="px-4 py-3 text-sm text-stone-600 whitespace-nowrap">{formatDate(e.expense_date)}</td>
                    <td className="px-4 py-3 text-sm text-stone-600 hidden lg:table-cell">{e.event?.name || <span className="text-stone-300">-</span>}</td>
                    <td className="px-4 py-3 text-sm text-stone-600 hidden xl:table-cell">
                      <div>{e.paid_to || e.vendor?.name || <span className="text-stone-300">-</span>}</div>
                      {e.payment_method && <div className="text-xs text-stone-400">{paymentMethodLabel(e.payment_method, methods)}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">{receiptCell(e)}</td>
                    <td className={`px-4 py-3 text-right font-bold text-sm tabular-nums whitespace-nowrap ${e.is_vendor_payment ? "text-stone-400" : "text-amber-700"}`}>
                      {formatCurrencyFull(e.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">{actions(e)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-sm">
                    <span className="font-bold text-stone-800">Total ({counted.length} expense{counted.length === 1 ? "" : "s"})</span>
                    {counted.length !== filtered.length && <span className="text-xs text-stone-500 ml-2">vendor payments not included</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-amber-800 tabular-nums">{formatCurrencyFull(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <ExpenseModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        expense={editing}
        onExpenseSaved={loadData}
      />

      <DeleteConfirmationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete expense"
        message={`Delete “${deleting?.title}” (${formatCurrencyFull(deleting?.amount)})? Its receipt is deleted too. This can't be undone.`}
        loading={isDeleting}
      />
    </DashboardLayout>
  );
}
